import { ReactElement, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useGetTenantDetailQuery } from '../../../hooks/queries/useGetTenantDetailQuery';
import { useUpgradeTenantToPartnerMutation } from '../../../hooks/mutations';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import classes from './UpgradeTenantToPartnerPage.module.css';

const PAGE_TITLE = 'Oppgrader til partner';
const CASHBACK_LABEL = 'Cashback-prosent (%)';
const CASHBACK_PLACEHOLDER = 'F.eks. 20 for 20%';
const CONTACT_EMAIL_LABEL = 'Kontakt e-post (valgfri)';
const CONTACT_EMAIL_PLACEHOLDER = 'Standard: første ORG_ADMIN sin e-post';
const CONTACT_PHONE_LABEL = 'Kontakt telefon (valgfri)';
const CONTACT_PHONE_PLACEHOLDER = '+47 123 45 678';
const ADMIN_USERS_LABEL = 'Velg brukere som skal få PARTNER_ADMIN-rolle';
const SAVE_BUTTON_TEXT = 'Oppgrader til partner';
const ERROR_MESSAGE = 'Kunne ikke laste kundedata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';
const ALREADY_PARTNER_MESSAGE = 'Denne kunden er allerede en partner.';
const BACK_LINK_LABEL = 'Tilbake til kundedetaljer';
const NO_ADMIN_SELECTED_ERROR = 'Du må velge minst én bruker som skal få PARTNER_ADMIN-rolle';

export const UpgradeTenantToPartnerPage = (): ReactElement => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { data: tenant, isLoading, error } = useGetTenantDetailQuery(Number(tenantId));
  const upgradeTenantToPartnerMutation = useUpgradeTenantToPartnerMutation();

  const [cashbackPercent, setCashbackPercent] = useState<string>('20');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  const backLink = Routes.SystemAdminTenantDetail.replace(':tenantId', tenantId || '');

  const handleSave = () => {
    if (!tenantId) return;

    if (selectedUserIds.size === 0) {
      toast.error(NO_ADMIN_SELECTED_ERROR);
      return;
    }

    const cashbackValue = Number(cashbackPercent);
    if (isNaN(cashbackValue) || cashbackValue < 0 || cashbackValue > 100) {
      toast.error('Cashback-prosent må være mellom 0 og 100');
      return;
    }

    // Convert percentage to basis points (20% = 2000 basis points)
    const cashbackBasisPoints = Math.round(cashbackValue * 100);

    upgradeTenantToPartnerMutation.mutate(
      {
        tenantId: Number(tenantId),
        payload: {
          cashbackPercentage: cashbackBasisPoints,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          adminUserIds: Array.from(selectedUserIds),
        },
      },
      {
        onSuccess: (response) => {
          toast.success(
            `${tenant?.name} er nå oppgradert til partner! ${response.emailsSent} e-poster sendt.`
          );
          navigate(backLink);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || 'Kunne ikke oppgradere til partner. Prøv igjen senere.';
          toast.error(errorMessage);
        },
      }
    );
  };

  const handleCashbackChange = (value: string) => {
    // Allow empty, digits, and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCashbackPercent(value);
    }
  };

  const handleUserToggle = (userId: number) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <Alert variant="danger" size="md">
        <Paragraph>{ERROR_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  if (!tenant) {
    return (
      <Alert variant="warning" size="md">
        <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  if (tenant.isPartner) {
    return (
      <FocusPageLayout
        title={PAGE_TITLE}
        subtitle={tenant.name}
        backLink={backLink}
        backLinkLabel={BACK_LINK_LABEL}
      >
        <Alert variant="warning" size="md">
          <Paragraph>{ALREADY_PARTNER_MESSAGE}</Paragraph>
        </Alert>
      </FocusPageLayout>
    );
  }

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      subtitle={`Oppgrader ${tenant.name} til å også være partner`}
      backLink={backLink}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <div className={classes.form}>
        <div className={classes.section}>
          <TextField
            label={CASHBACK_LABEL}
            type="text"
            name="cashbackPercent"
            inputMode="decimal"
            value={cashbackPercent}
            onChange={(e) => handleCashbackChange(e.target.value)}
            placeholder={CASHBACK_PLACEHOLDER}
            required
          />
          <Paragraph className={classes.helperText}>
            Cashback-prosenten som partneren mottar på kundesalg (0-100%)
          </Paragraph>
        </div>

        <div className={classes.section}>
          <TextField
            label={CONTACT_EMAIL_LABEL}
            type="email"
            name="contactEmail"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder={CONTACT_EMAIL_PLACEHOLDER}
          />
        </div>

        <div className={classes.section}>
          <TextField
            label={CONTACT_PHONE_LABEL}
            type="tel"
            name="contactPhone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder={CONTACT_PHONE_PLACEHOLDER}
          />
        </div>

        <div className={classes.section}>
          <label className={classes.fieldLabel}>{ADMIN_USERS_LABEL}</label>
          <div className={classes.userList}>
            {tenant.users.length === 0 ? (
              <Paragraph className={classes.noUsers}>
                Ingen brukere funnet for denne kunden.
              </Paragraph>
            ) : (
              tenant.users.map((user) => (
                <div key={user.id} className={classes.userItem}>
                  <label className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      className={classes.checkbox}
                    />
                    <span className={classes.userInfo}>
                      <span className={classes.userEmail}>{user.email}</span>
                      {user.isPending && (
                        <span className={classes.pendingBadge}>Venter</span>
                      )}
                      <span className={classes.userRole}>{user.role}</span>
                    </span>
                  </label>
                </div>
              ))
            )}
          </div>
          {selectedUserIds.size === 0 && (
            <Paragraph className={classes.helperText}>
              Velg minst én bruker som skal få PARTNER_ADMIN-rolle
            </Paragraph>
          )}
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            onClick={() => navigate(backLink)}
            disabled={upgradeTenantToPartnerMutation.isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={upgradeTenantToPartnerMutation.isPending || selectedUserIds.size === 0}
          >
            {upgradeTenantToPartnerMutation.isPending ? 'Oppgraderer...' : SAVE_BUTTON_TEXT}
          </Button>
        </div>
      </div>
    </FocusPageLayout>
  );
};
